import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import type { EChartsOption, SeriesOption } from 'echarts';
import { SingleQuestionResponsesComponent } from '../single-question-responses/single-question-responses.component';
import { MatDialog } from '@angular/material/dialog';
import { SurveyService } from '../../../services/surveys.service';
import { TranslateService } from '@ngx-translate/core';
import mermaid, { MermaidConfig } from 'mermaid';

interface MetricConfig {
  labelKey: string;
  color: string;
  getValue: (q: any) => number;
}

@Component({
  selector: 'app-question-history-dashboard',
  templateUrl: './question-history-dashboard.component.html',
  styleUrls: ['./question-history-dashboard.component.scss']
})
export class QuestionHistoryDashboardComponent implements OnInit, AfterViewInit {
  @Input() questionHistory: any;
  // accordionOpened non serve più per l'apertura, ma lo lasciamo per compatibilità inputs
  @Input() accordionOpened = true;
  @ViewChild('mermaid', { static: false }) mermaidDiv!: ElementRef;

  loading = false;

  chartOptions: EChartsOption = {};
  currentChartType: 'bar' | 'line' = 'bar';
  availableChartTypes: ('bar' | 'line')[] = [];

  mermaidDefinition: string | null = null;
  currentSurvey: any = null;
  surveys: any[] = [];
  surveysToCompare: any[] = [];
  isSmooth = false;

  // Configurazione Mermaid aggiornata per stile curve e colori
  mermaidConfig: MermaidConfig = {
    startOnLoad: false,
    flowchart: { useMaxWidth: true, htmlLabels: true, curve: 'basis' },
    securityLevel: 'loose',
    theme: 'base',
    themeVariables: {
      primaryColor: '#9169db',
      primaryTextColor: '#fff',
      primaryBorderColor: '#5e4391',
      lineColor: '#9169db',
      secondaryColor: '#f4f0fc',
      tertiaryColor: '#fff'
    }
  };

  constructor(
    private dialog: MatDialog,
    private surveyService: SurveyService,
    protected translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  get question() {
    return this.questionHistory?.questions ? this.questionHistory.questions[0] : null;
  }

  getIconByType(): string {
    const type = this.question?.typology;

    if (this.surveyService.isConsensusQuestion(type)) { return 'insights'; }
    if (this.surveyService.isQuantitativeQuestion(type)) { return 'functions'; }

    switch (type) {
      case 'MultipleChoice':
        return 'donut_large';
      case 'Checkbox':
        return 'checklist';
      case 'Grid':
        return 'table_chart';
      case 'Open':
      case 'OpenText':
        return 'rate_review';
      default:
        return 'analytics';
    }
  }

  ngOnInit(): void {
    if (this.questionHistory?.questions?.length) {
      this.surveys = this.questionHistory.questions.map((q: any) => ({
        ...q.survey,
        survey_question: q
      })).sort((a: any, b: any) =>
        a.major_version !== b.major_version
          ? a.major_version - b.major_version
          : a.minor_version - b.minor_version
      );

      this.currentSurvey = this.surveys[0];
      this.initChartCapabilities();

      if (this.availableChartTypes.length > 0) {
        this.buildChartOptions();
      }
    }
  }

  ngAfterViewInit(): void {
    this.renderMermaidDiagram();
  }

  private initChartCapabilities() {
    const typology = this.question?.typology;
    this.availableChartTypes = [];

    if (this.surveyService.isConsensusQuestion(typology)) {
      this.availableChartTypes = ['bar', 'line'];
    }
    else if (this.surveyService.isQuantitativeQuestion(typology)) {
      this.availableChartTypes = ['bar', 'line'];
    }
    else {
      this.availableChartTypes = [];
    }

    if (this.availableChartTypes.length > 0) {
      this.currentChartType = this.availableChartTypes[0];
    }
  }

  /**
   * Palette Aggiornata: #9169db (Primary)
   */
  private getMetricsConfig(): MetricConfig[] {
    const typology = this.question?.typology;

    // 1. CONSENSUS
    if (this.surveyService.isConsensusQuestion(typology)) {
      return [
        {
          labelKey: 'generic.certainty-degree',
          color: '#9169db', // Primary Purple
          getValue: (q) => q.results?.certainty_degree ?? 0
        },
        {
          labelKey: 'generic.contradiction-degree',
          color: '#26a69a', // Teal (Complementare per contrasto)
          getValue: (q) => q.results?.contradiction_degree ?? 0
        },
        {
          labelKey: 'components.responses.consensus.agree',
          color: '#ffca28', // Giallo ambrato (Rimane per "attenzione")
          getValue: (q) => {
            if (q.results && Array.isArray(q.responses) && q.responses.length > 0) {
              const agreeCount = q.responses.filter((r: any) => r?.answer === 'AGREE').length;
              return (agreeCount / q.responses.length) * 100;
            }
            return q.results?.a_ratio ?? 0;
          }
        }
      ];
    }

    // 2. QUANTITATIVO
    if (this.surveyService.isQuantitativeQuestion(typology)) {
      return [
        { labelKey: 'generic.average', color: '#9169db', getValue: (q) => q.results?.mean ?? 0 }, // Purple
        { labelKey: 'generic.geometric-average', color: '#b39ddb', getValue: (q) => q.results?.geometric_mean ?? 0 }, // Lighter Purple
        { labelKey: 'generic.median', color: '#ec407a', getValue: (q) => q.results?.median ?? 0 }, // Pink
        { labelKey: 'generic.variance', color: '#ffb74d', getValue: (q) => q.results?.variance ?? 0 } // Orange
      ];
    }

    return [];
  }

  private buildChartOptions() {
    const metrics = this.getMetricsConfig();
    const questions = this.questionHistory.questions;

    if (!metrics.length || !questions) { return; }

    let revCounter = 1;
    const xLabels = questions.map((q: any, i: number) => {
      const label = `Session ${q?.survey?.session_number}`;
      const prev = questions[i - 1];
      if (!prev) { return label; }

      if (prev?.final_decision === 'revised') { return label + `\n(REV ${revCounter++})`; }
      if (prev?.final_decision === 'revised_split') { return label + `\n(SPLIT)`; }
      return label;
    });

    const series: SeriesOption[] = metrics.map(metric => {
      const dataValues = questions.map((q: any) => {
        const val = metric.getValue(q);
        return val != null ? Number(val).toFixed(2) : '0.00';
      });

      return {
        name: this.translateService.instant(metric.labelKey),
        type: this.currentChartType,
        smooth: this.currentChartType === 'line' ? this.isSmooth : false,
        // Area sfumata con il colore della metrica
        areaStyle: this.currentChartType === 'line' ? {
          opacity: 0.2,
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color: metric.color }, { offset: 1, color: 'rgba(255,255,255,0)' }]
          }
        } : undefined,
        barMaxWidth: 40, // Barre leggermente più sottili ed eleganti
        itemStyle: {
          color: metric.color,
          borderRadius: this.currentChartType === 'bar' ? [6, 6, 0, 0] : 4
        },
        data: dataValues
      };
    });

    this.chartOptions = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#eef0f5',
        borderWidth: 1,
        textStyle: { color: '#333' },
        extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-radius: 8px;', // Ombra tooltip
        formatter: (params: any) => {
          let html = `<div style="font-weight:700; color: #5e4391; margin-bottom:8px; padding-bottom:4px; border-bottom:1px solid #eee;">
                        ${params[0].axisValueLabel}
                      </div>`;
          params.forEach((p: any) => {
            html += `<div style="display:flex; justify-content:space-between; align-items:center; min-width:140px; margin-bottom:4px;">
                      <div style="display:flex; align-items:center;">
                        <span style="display:inline-block; width:10px; height:10px; border-radius:50%; background-color:${p.color}; margin-right:8px;"></span>
                        <span style="font-size:12px; color:#666;">${p.seriesName}</span>
                      </div>
                      <span style="font-weight:bold; font-size:13px; margin-left:12px;">${p.value}${this.surveyService.isConsensusQuestion(this.question.typology) ? '%' : ''}</span>
                     </div>`;
          });
          return html;
        }
      },
      legend: {
        data: series.map((s: any) => s.name),
        bottom: 0,
        icon: 'roundRect', // Icona legenda più moderna
        itemWidth: 12,
        itemHeight: 12,
        textStyle: { color: '#666' }
      },
      grid: { left: '3%', right: '4%', bottom: '12%', top: '8%', containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: this.currentChartType === 'bar',
        data: xLabels,
        axisLine: { show: false }, // Meno rumore visivo
        axisTick: { show: false },
        axisLabel: { color: '#888', fontWeight: 'bold' }
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } }, // Linee griglia molto leggere
        axisLabel: { color: '#999' }
      },
      series
    };
  }

  setChartType(type: 'line' | 'bar') {
    if (this.availableChartTypes.includes(type)) {
      this.currentChartType = type;
      this.buildChartOptions();
    }
  }

  // --- Mermaid Logic ---

  renderMermaidDiagram(): void {
    mermaid.initialize(this.mermaidConfig);
    const el = this.mermaidDiv?.nativeElement;

    if (el) {
      this.mermaidDefinition = this.generateMermaidDiagram();
      mermaid.render('generatedDiagram', this.mermaidDefinition).then((result) => {
        el.innerHTML = result.svg;
        // @ts-ignore
        result.bindFunctions?.();

        const svg = el.querySelector('svg');
        if (svg) {
          const nodes = svg.querySelectorAll('.node');
          svg.style.display = 'block';
          svg.style.margin = '0 auto';

          nodes.forEach(node => {
            node.addEventListener('click', () => {
              const rawId = node.id;
              const match = rawId.match(/S_(\d+)/);
              if (match) {
                const surveyId = parseInt(match[1], 10);
                this.onNodeClick(surveyId);
              }
            });
            node.addEventListener('mouseenter', () => { node.style.cursor = 'pointer'; });
            node.addEventListener('mouseleave', () => { node.style.fill = ''; });
          });
        }
      }).catch(err => console.error('Mermaid error:', err));
    }
  }

  onNodeClick(surveyID: number) {
    this.currentSurvey = this.surveys?.find((survey) => surveyID == survey?.id);
    this.renderMermaidDiagram();
  }

  generateMermaidDiagram(): string {
    const surveyMap = new Map<string, any>();
    this.surveys.forEach(s => surveyMap.set(`${s.major_version}.${s.minor_version}`, s));

    const nodes: string[] = [];
    const links: string[] = [];

    this.surveys.forEach(s => {
      const nodeId = `S_${s.id}`;
      // Versione più compatta della label
      const label = `v${s.version_number}`;

      let styledNode = s.is_draft
        ? `${nodeId}("${label}")`
        : `${nodeId}("${label}"):::started`;

      if (s.id === this.currentSurvey?.id) {
        styledNode = `${nodeId}("${label}"):::current`;
      }

      nodes.push(styledNode);

      if (s.minor_version > 0) {
        const prevMinorKey = `${s.major_version}.${s.minor_version - 1}`;
        const prevSurvey = surveyMap.get(prevMinorKey);
        if (prevSurvey) { links.push(`S_${prevSurvey.id} --> S_${s.id}`); }
      } else if (s.major_version > 1) {
        const prevMajorKey = `${s.major_version - 1}.0`;
        const prevSurvey = surveyMap.get(prevMajorKey);
        if (prevSurvey) { links.push(`S_${prevSurvey.id} --> S_${s.id}`); }
      }
    });

    // Stili CSS Mermaid aggiornati al viola
    const styleBlock = [
      'classDef started fill:#f4f0fc,stroke:#9169db,stroke-width:1px,color:#5e4391;',
      'classDef current fill:#9169db,stroke:#9169db,stroke-width:2px,color:#fff;'
    ].join('\n');

    return ['flowchart LR', ...nodes, ...links, styleBlock].join('\n');
  }

  get timelineIsDisabled(): boolean {
    return this.availableChartTypes.length === 0;
  }

  onChartClick(event: any) {
    const question = this.questionHistory?.questions[event?.dataIndex];
    if (question) {
      this.dialog.open(SingleQuestionResponsesComponent, {
        panelClass: 'fullscreen-dialog',
        data: {
          questionID: question.id,
          showPrivateInfo: true,
          isDialog: true
        }
      });
    }
  }

  setSmooth(smooth: boolean) {
    this.isSmooth = smooth;
    this.buildChartOptions();
  }
}
